import { createElement } from "react";
import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type FeatureShellSlots,
  type HttpClient,
  type ServiceBinding,
  type ServiceContainer,
} from "@nasam/kernel";
import { createNotificationsHttpRepository } from "./data";
import type { NotificationsRepository } from "./domain";
import { NotificationsPanel } from "./presentation/NotificationsPanel";

export const notificationsRepositoryToken =
  createServiceToken<NotificationsRepository>(
    "notifications.notifications-repository",
  );

const NOTIFICATIONS_VIEW =
  "core.notification_management.notification_management_list.view";

function bindings(httpClient: HttpClient): readonly ServiceBinding[] {
  return [
    bindService(
      notificationsRepositoryToken,
      createNotificationsHttpRepository(httpClient),
    ),
  ];
}

const slotsByContainer = new WeakMap<ServiceContainer, FeatureShellSlots>();

// The shell keys rendered slots by component identity; a fresh closure per
// call would remount the panel (and tear down its FCM token registration)
// every time permissions or the service container recompute.
function shellSlotsFor(services: ServiceContainer): FeatureShellSlots {
  let slots = slotsByContainer.get(services);
  if (slots === undefined) {
    // tryResolve, not resolve: a default/empty container (e.g. AppShell's
    // fallback) has no binding here, and this must render nothing, not throw.
    const TopBarEnd = () => {
      const repository = services.tryResolve(notificationsRepositoryToken);
      return repository === undefined
        ? null
        : createElement(NotificationsPanel, { repository });
    };
    slots = { topBarEnd: TopBarEnd };
    slotsByContainer.set(services, slots);
  }
  return slots;
}

export const notificationsFeature: AppFeatureDefinition = {
  id: "notifications",
  version: "0.1.0",
  titleKey: "notifications:moduleTitle",
  icon: "Bell",
  basePath: "/notifications",
  requiredPermissions: [NOTIFICATIONS_VIEW],
  // No rail icon and no routes — the panel is reached from the top-bar bell
  // only (see shellSlots, added in Task 4). An explicit empty array (not
  // `undefined`) stops AppShell from synthesizing a default rail node that
  // would point at a basePath with no route behind it.
  navigation: [],
  routes: () => [],
  shellSlots: shellSlotsFor,
  i18n: {
    namespace: "notifications",
    load: {
      en: () => import("../../i18n/locales/en/notifications"),
      ar: () => import("../../i18n/locales/ar/notifications"),
    },
  },
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "notifications:permissions.core",
      children: [
        {
          segment: "notification_management",
          labelKey: "notifications:permissions.management",
          children: [
            {
              segment: "notification_management_list",
              labelKey: "notifications:permissions.notifications",
              actions: [{ id: "view", labelKey: "notifications:actions.view" }],
            },
          ],
        },
      ],
    },
  ],
  provideServices: ({ httpClient }) => bindings(httpClient),
};
